import React, { useState, useEffect } from 'react';
import { templateService, clientService, userService, conflictService, searchClientsAsync } from '../services/api';
import SearchableSelect from './common/SearchableSelect';
import AsyncSearchableSelect from './common/AsyncSearchableSelect';
import ConflictWarningDialog from './conflicts/ConflictWarningDialog';
import {
  X,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Building,
  User,
  FileText,
  Briefcase
} from 'lucide-react';
import useModal from '../hooks/useModal';
import SimpleCaseModal from './SimpleCaseModal';

const CreateCaseModal = ({ isOpen, onClose, onSuccess, preSelectedClientId = null }) => {
  const [step, setStep] = useState(0); // 0: Choose Method, 1: Select Template, 2: Customize, 3: Case Details, 4: Scheduling
  const [showSimpleModal, setShowSimpleModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [preSelectedClientName, setPreSelectedClientName] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  // Form data
  const [caseForm, setCaseForm] = useState({
    title: '',
    description: '',
    case_number: '',
    client_id: preSelectedClientId || '',
    assigned_attorney_id: '',
    billing_type: '',
    hourly_rate: '',
    estimated_hours: '',
    flat_fee_amount: '',
    contingency_percentage: '',
    selected_project_ids: []
  });
  
  // Scheduling preferences
  const [schedulingPrefs, setSchedulingPrefs] = useState({
    auto_schedule_tasks: true,
    start_date: null,
    work_hours_per_day: 8.0,
    respect_dependencies: true,
    default_start_time: '09:00',
    default_end_time: '17:00',
    allow_weekend_scheduling: false,
    assign_to_current_user: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchTemplatesAndAttorneys();
      // Set the pre-selected client ID when modal opens
      if (preSelectedClientId) {
        setCaseForm(prev => ({ ...prev, client_id: preSelectedClientId }));
      }
    }
  }, [isOpen, preSelectedClientId]);

  useEffect(() => {
    if (preSelectedClientId) {
      clientService.getClient(preSelectedClientId).then(res => {
        const c = res.data;
        setPreSelectedClientName([c.first_name, c.last_name].filter(Boolean).join(' '));
      }).catch(() => {});
    }
  }, [preSelectedClientId]);

  const fetchTemplatesAndAttorneys = async () => {
    setLoading(true);
    try {
      const templatesResponse = await templateService.getCaseTemplates();
      setTemplates(templatesResponse.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load templates');
    }
    // Fetch attorneys separately so a missing permission doesn't break the whole form
    try {
      const attorneysResponse = await userService.getUsers(false, 'Attorney');
      const activeAttorneys = (attorneysResponse.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
      setAttorneys(activeAttorneys);
    } catch (error) {
      console.error('Could not load attorneys:', error);
      setAttorneys([]);
    }
    setLoading(false);
  };

  const selectTemplate = async (template) => {
    setSelectedTemplate(template);
    setLoading(true);
    try {
      const response = await templateService.getCaseTemplatePreview(template.id);
      setTemplatePreview(response.data);
      
      // Pre-fill form with template defaults
      setCaseForm(prev => ({
        ...prev,
        billing_type: template.default_billing_type || '',
        hourly_rate: template.default_hourly_rate || '',
        estimated_hours: template.default_estimated_hours || '',
        flat_fee_amount: template.default_flat_fee_amount || '',
        contingency_percentage: template.default_contingency_percentage || '',
        selected_project_ids: response.data.projects.map(p => p.id)
      }));
      
      setStep(2);
    } catch (error) {
      console.error('Error fetching template preview:', error);
      setError('Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectToggle = (projectId) => {
    setCaseForm(prev => ({
      ...prev,
      selected_project_ids: prev.selected_project_ids.includes(projectId)
        ? prev.selected_project_ids.filter(id => id !== projectId)
        : [...prev.selected_project_ids, projectId]
    }));
  };

  const doCreateCase = async () => {
    // Convert form data to proper types for API
    const apiData = {
      ...caseForm,
      assigned_attorney_id: caseForm.assigned_attorney_id || null,
      hourly_rate: caseForm.hourly_rate ? parseFloat(caseForm.hourly_rate) : null,
      estimated_hours: caseForm.estimated_hours ? parseInt(caseForm.estimated_hours) : null,
      flat_fee_amount: caseForm.flat_fee_amount ? parseFloat(caseForm.flat_fee_amount) : null,
      contingency_percentage: caseForm.contingency_percentage ? parseFloat(caseForm.contingency_percentage) : null,
      case_number: caseForm.case_number || null,
      description: caseForm.description || null,
      scheduling_preferences: schedulingPrefs.auto_schedule_tasks ? {
        ...schedulingPrefs,
        start_date: schedulingPrefs.start_date ? new Date(schedulingPrefs.start_date).toISOString() : null
      } : null
    };

    const response = await templateService.createCaseFromTemplate(
      selectedTemplate.id,
      apiData
    );

    onSuccess(response.data);
    resetForm();
    onClose();
  };

  const handleCreateCase = async () => {
    setLoading(true);
    setError('');

    try {
      // Run conflict check on selected client before creating case
      if (caseForm.client_id) {
        try {
          const clientRes = await clientService.getClient(caseForm.client_id);
          const selectedClient = clientRes.data;
          if (selectedClient) {
            try {
              const checkResponse = await conflictService.checkConflicts({
                first_name: selectedClient.first_name,
                last_name: selectedClient.last_name,
                company_name: selectedClient.company_name,
                email: selectedClient.email,
                phone: selectedClient.phone,
                check_type: 'case_creation',
              });
              if (checkResponse.data.blocking) {
                setConflictData({
                  conflictCheckId: checkResponse.data.conflict_check_id,
                  matches: checkResponse.data.matches || [],
                });
                setShowConflictDialog(true);
                setLoading(false);
                return;
              }
            } catch (conflictErr) {
              console.error('Conflict check failed, proceeding:', conflictErr);
            }
          }
        } catch (clientErr) {
          // If client fetch fails, proceed with case creation
          console.error('Client fetch for conflict check failed, proceeding:', clientErr);
        }
      }

      await doCreateCase();
    } catch (error) {
      console.error('Error creating case:', error);
      let errorMessage = 'Failed to create case';

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = 'Validation error occurred';
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConflictOverride = async () => {
    setLoading(true);
    try {
      await doCreateCase();
    } catch (error) {
      console.error('Error creating case after override:', error);
      setError('Failed to create case');
    } finally {
      setLoading(false);
      setShowConflictDialog(false);
    }
  };

  const resetForm = () => {
    setStep(0);
    setSelectedTemplate(null);
    setTemplatePreview(null);
    setShowSimpleModal(false);
    setCaseForm({
      title: '',
      description: '',
      case_number: '',
      client_id: preSelectedClientId || '',
      assigned_attorney_id: '',
      billing_type: '',
      hourly_rate: '',
      estimated_hours: '',
      flat_fee_amount: '',
      contingency_percentage: '',
      selected_project_ids: []
    });
    setSchedulingPrefs({
      auto_schedule_tasks: true,
      start_date: null,
      work_hours_per_day: 8.0,
      respect_dependencies: true,
      default_start_time: '09:00',
      default_end_time: '17:00',
      allow_weekend_scheduling: false,
      assign_to_current_user: true
    });
    setError('');
  };

  const handleSimpleCaseSuccess = (newCase) => {
    onSuccess(newCase);
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // If the simple modal is open, render it instead
  if (showSimpleModal) {
    return (
      <SimpleCaseModal
        isOpen={true}
        onClose={() => {
          setShowSimpleModal(false);
          setStep(0);
        }}
        onSuccess={handleSimpleCaseSuccess}
        preSelectedClientId={preSelectedClientId}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      {/* Backdrop that doesn't cover navbar */}
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {step === 0 ? 'Create New Case' : 'Create Case from Template'}
            </h2>
            {step > 0 && (
              <div className="flex items-center mt-2 space-x-4 text-sm">
                <span className={`flex items-center ${step >= 1 ? 'text-yellow-600' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                    step >= 1 ? 'bg-yellow-500 text-white' : 'bg-gray-200'
                  }`}>1</span>
                  Select Template
                </span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <span className={`flex items-center ${step >= 2 ? 'text-yellow-600' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                    step >= 2 ? 'bg-yellow-500 text-white' : 'bg-gray-200'
                  }`}>2</span>
                  Customize Projects
                </span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <span className={`flex items-center ${step >= 3 ? 'text-yellow-600' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                    step >= 3 ? 'bg-yellow-500 text-white' : 'bg-gray-200'
                  }`}>3</span>
                  Case Details
                </span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <span className={`flex items-center ${step >= 4 ? 'text-yellow-600' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                    step >= 4 ? 'bg-yellow-500 text-white' : 'bg-gray-200'
                  }`}>4</span>
                  Scheduling
                </span>
              </div>
            )}
          </div>
          <button onClick={handleClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          )}

          {/* Step 0: Choose Creation Method */}
          {step === 0 && !loading && (
            <div>
              <h3 className="text-lg font-medium mb-4 text-center">How would you like to create this case?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Template-based Option */}
                <div
                  onClick={() => setStep(1)}
                  className="border-2 border-gray-200 rounded-lg p-6 hover:border-yellow-400 hover:bg-yellow-50 cursor-pointer transition-all group"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-yellow-200 transition-colors">
                      <FileText className="h-8 w-8 text-yellow-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 text-lg mb-2">Use a Template</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Start with a pre-configured template that includes projects, tasks, and automated workflows
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 text-green-600 mr-1" />
                        <span>Pre-defined projects & tasks</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 text-green-600 mr-1" />
                        <span>Automated scheduling</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 text-green-600 mr-1" />
                        <span>Workflow automation</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Empty Case Option */}
                <div
                  onClick={() => setShowSimpleModal(true)}
                  className="border-2 border-gray-200 rounded-lg p-6 hover:border-green-400 hover:bg-green-50 cursor-pointer transition-all group"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                      <Briefcase className="h-8 w-8 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 text-lg mb-2">Start from Scratch</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Create an empty case and manually add projects and tasks as needed
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 text-green-600 mr-1" />
                        <span>Quick & simple setup</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 text-green-600 mr-1" />
                        <span>Maximum flexibility</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 text-green-600 mr-1" />
                        <span>Custom workflow</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Template Selection */}
          {step === 1 && !loading && (
            <div>
              <h3 className="text-lg font-medium mb-4">Choose a Case Template</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => selectTemplate(template)}
                    className="border border-gray-200 rounded-lg p-4 hover:border-yellow-300 hover:bg-yellow-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                      <span className="flex items-center text-xs text-gray-500">
                        {template.scope === 'firm' ? <Building className="h-3 w-3 mr-1" /> : <User className="h-3 w-3 mr-1" />}
                        {template.scope}
                      </span>
                    </div>

                    {template.description && (
                      <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                    )}

                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      {template.practice_area && (
                        <span className="bg-gray-100 px-2 py-1 rounded">
                          {template.practice_area}
                        </span>
                      )}
                      {template.default_billing_type && (
                        <span className="flex items-center">
                          <DollarSign className="h-3 w-3 mr-1" />
                          {template.default_billing_type}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-start mt-6">
                <button
                  onClick={() => setStep(0)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back to Options
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Project Customization */}
          {step === 2 && templatePreview && !loading && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Customize Projects for "{selectedTemplate.name}"</h3>
                <p className="text-sm text-gray-600">
                  Select which projects to include and review the automated actions that will be created.
                </p>
              </div>

              {/* Template Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Template Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-700">Projects</span>
                    <p className="font-medium">{templatePreview.summary.total_projects}</p>
                  </div>
                  <div>
                    <span className="text-gray-700">Est. Hours</span>
                    <p className="font-medium">{templatePreview.summary.total_estimated_hours}h</p>
                  </div>
                  <div>
                    <span className="text-gray-700">Est. Value</span>
                    <p className="font-medium">${templatePreview.summary.total_estimated_value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <span className="text-gray-700">Actions</span>
                    <p className="font-medium">{templatePreview.summary.total_actions}</p>
                  </div>
                </div>
              </div>

              {/* Projects List */}
              <div className="space-y-4">
                {templatePreview.projects.map((project, index) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={caseForm.selected_project_ids.includes(project.id)}
                          onChange={() => handleProjectToggle(project.id)}
                          aria-label={`Include project: ${project.name}`}
                          className="mr-3 h-4 w-4 text-yellow-600 rounded border-gray-300"
                        />
                        <div>
                          <h4 className="font-medium text-gray-900">{project.name}</h4>
                          {project.description && (
                            <p className="text-sm text-gray-600">{project.description}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        Step {index + 1}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3 text-sm">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-1" />
                        <span>{project.estimated_hours || 0} hours</span>
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                        <span>${project.estimated_value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                        <span>{project.actions.length} actions</span>
                      </div>
                    </div>

                    {/* Dependencies */}
                    {project.dependencies.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1">Depends on:</p>
                        <div className="flex flex-wrap gap-1">
                          {project.dependencies.map(dep => (
                            <span key={dep.id} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              {dep.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions Preview */}
                    {project.actions.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Automated Actions:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {project.actions.slice(0, 4).map(action => (
                            <div key={action.id} className="text-xs bg-gray-50 p-2 rounded">
                              <span className="font-medium capitalize">{action.action_type.replace('_', ' ')}</span>
                              <span className={`ml-2 px-1 py-0.5 rounded text-xs ${
                                action.execution_type === 'automatic' ? 'bg-green-100 text-green-700' :
                                action.execution_type === 'suggested' ? 'bg-gray-100 text-gray-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {action.execution_type}
                              </span>
                            </div>
                          ))}
                          {project.actions.length > 4 && (
                            <div className="text-xs text-gray-500 col-span-2">
                              +{project.actions.length - 4} more actions...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back to Templates
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={caseForm.selected_project_ids.length === 0}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Continue to Case Details
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Case Details */}
          {step === 3 && !loading && (
            <div>
              <h3 className="text-lg font-medium mb-4">Case Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Basic Information</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Case Title *
                    </label>
                    <input
                      type="text"
                      value={caseForm.title}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Enter case title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <AsyncSearchableSelect
                      value={caseForm.client_id}
                      onChange={(val) => setCaseForm(prev => ({ ...prev, client_id: val }))}
                      onSearch={searchClientsAsync}
                      emptyOption="Select a client"
                      required
                      disabled={!!preSelectedClientId}
                      selectedLabel={preSelectedClientName}
                    />
                    {preSelectedClientId && (
                      <p className="text-xs text-gray-500 mt-1">Client is pre-selected from the current page</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned Attorney
                    </label>
                    <SearchableSelect
                      value={caseForm.assigned_attorney_id}
                      onChange={(val) => setCaseForm(prev => ({ ...prev, assigned_attorney_id: val }))}
                      options={Array.isArray(attorneys) ? attorneys.map(attorney => ({
                        value: attorney.id,
                        label: `${attorney.first_name} ${attorney.last_name}`
                      })) : []}
                      emptyOption="Assign to me (default)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Case Number
                    </label>
                    <input
                      type="text"
                      value={caseForm.case_number}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, case_number: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Auto-generated if left blank"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={caseForm.description}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      rows="3"
                      placeholder="Optional case description"
                    />
                  </div>
                </div>

                {/* Billing Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Billing Configuration</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Type
                    </label>
                    <select
                      value={caseForm.billing_type}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, billing_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Select billing type</option>
                      <option value="hourly">Hourly</option>
                      <option value="flat_fee">Flat Fee</option>
                      <option value="contingency">Contingency</option>
                      <option value="retainer">Retainer</option>
                    </select>
                  </div>

                  {caseForm.billing_type === 'hourly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hourly Rate ($)
                      </label>
                      <input
                        type="number"
                        value={caseForm.hourly_rate}
                        onChange={(e) => setCaseForm(prev => ({ ...prev, hourly_rate: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="350.00"
                      />
                    </div>
                  )}

                  {caseForm.billing_type === 'flat_fee' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Flat Fee Amount ($)
                      </label>
                      <input
                        type="number"
                        value={caseForm.flat_fee_amount}
                        onChange={(e) => setCaseForm(prev => ({ ...prev, flat_fee_amount: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="5000.00"
                      />
                    </div>
                  )}

                  {caseForm.billing_type === 'contingency' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contingency Percentage (%)
                      </label>
                      <input
                        type="number"
                        value={caseForm.contingency_percentage}
                        onChange={(e) => setCaseForm(prev => ({ ...prev, contingency_percentage: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="33.33"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      value={caseForm.estimated_hours}
                      onChange={(e) => setCaseForm(prev => ({ ...prev, estimated_hours: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="40"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back to Projects
                </button>
                <button
                  onClick={() => setStep(4)}
                  disabled={!caseForm.title || !caseForm.client_id}
                  className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                >
                  Next: Scheduling
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Scheduling Preferences */}
          {step === 4 && !loading && (
            <div>
              <h3 className="text-lg font-medium mb-4">Task Scheduling Preferences</h3>
              
              <div className="space-y-6">
                {/* Auto-scheduling toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-gray-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Automatic Task Scheduling</h4>
                      <p className="text-sm text-gray-600">Automatically schedule all template tasks on your calendar</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={schedulingPrefs.auto_schedule_tasks}
                      onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, auto_schedule_tasks: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                  </label>
                </div>

                {/* Scheduling options - only show if auto-scheduling is enabled */}
                {schedulingPrefs.auto_schedule_tasks && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic scheduling settings */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Scheduling Settings</h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={schedulingPrefs.start_date || ''}
                          onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, start_date: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <p className="text-xs text-gray-500 mt-1">Leave blank to start scheduling tomorrow</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Work Hours Per Day
                        </label>
                        <select
                          value={schedulingPrefs.work_hours_per_day}
                          onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, work_hours_per_day: parseFloat(e.target.value) }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="4">4 hours</option>
                          <option value="6">6 hours</option>
                          <option value="8">8 hours</option>
                          <option value="10">10 hours</option>
                        </select>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="respect-dependencies"
                          checked={schedulingPrefs.respect_dependencies}
                          onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, respect_dependencies: e.target.checked }))}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <label htmlFor="respect-dependencies" className="ml-2 text-sm text-gray-700">
                          Respect task dependencies
                        </label>
                      </div>
                    </div>

                    {/* Work hours and preferences */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Work Hours</h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={schedulingPrefs.default_start_time}
                            onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, default_start_time: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={schedulingPrefs.default_end_time}
                            onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, default_end_time: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                        </div>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="allow-weekends"
                          checked={schedulingPrefs.allow_weekend_scheduling}
                          onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, allow_weekend_scheduling: e.target.checked }))}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <label htmlFor="allow-weekends" className="ml-2 text-sm text-gray-700">
                          Allow weekend scheduling
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="assign-to-current"
                          checked={schedulingPrefs.assign_to_current_user}
                          onChange={(e) => setSchedulingPrefs(prev => ({ ...prev, assign_to_current_user: e.target.checked }))}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <label htmlFor="assign-to-current" className="ml-2 text-sm text-gray-700">
                          Assign all tasks to me
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    {schedulingPrefs.auto_schedule_tasks ? (
                      <>
                        <p>✓ Tasks will be automatically scheduled on your calendar</p>
                        <p>✓ Scheduling will start {schedulingPrefs.start_date ? `on ${new Date(schedulingPrefs.start_date).toLocaleDateString()}` : 'tomorrow'}</p>
                        <p>✓ Work hours: {schedulingPrefs.default_start_time} - {schedulingPrefs.default_end_time} ({schedulingPrefs.work_hours_per_day}h/day)</p>
                        {schedulingPrefs.respect_dependencies && <p>✓ Task dependencies will be respected</p>}
                        {schedulingPrefs.allow_weekend_scheduling && <p>✓ Weekend scheduling allowed</p>}
                      </>
                    ) : (
                      <p>Tasks will be created but not automatically scheduled</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setStep(3)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Back to Details
                </button>
                <button
                  onClick={handleCreateCase}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Case...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Create Case with Scheduling
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConflictWarningDialog
        isOpen={showConflictDialog}
        onClose={() => setShowConflictDialog(false)}
        onOverride={handleConflictOverride}
        conflicts={conflictData?.matches || []}
        conflictCheckId={conflictData?.conflictCheckId}
        loading={loading}
      />
    </div>
  );
};

export default CreateCaseModal;